import { useEffect, useState } from "react";

/* ─────────────────────────────────────────────
   IMAGE IMPORTS — src/assets/images/blogs/
───────────────────────────────────────────── */
import HeroImg             from "../../assets/images/blogs/ev-fire.webp";
import HeroImgMobile       from "../../assets/images/blogs/ev-fire-mobile.webp";
import NewsletterBgDesktop from "../../assets/images/blogs/newsletter-bg.webp";
import NewsletterMicMobile from "../../assets/images/blogs/newsletter-bg-mobile.webp";
import InlineImgScooters   from "../../assets/images/blogs/ev-fire-scooters.webp";
import TweetImg            from "../../assets/images/blogs/tweet-ev-fire.webp";

/* ─────────────────────────────────────────────
   WhatIsTheDealWithEVsCatchingFireAndWhyShouldYouCare.jsx
   Prefix      : ef-
   Fonts       : Montserrat (headings) | Roboto (body)
   Purple      : #743FAC
   BG main     : #1A1A1A
   Inner pad   : 102px desktop | 32px tab | 16px mobile
   Breaks      : 768px | 480px
   Anim        : IntersectionObserver fade-up
                 delays 0.08s / 0.18s / 0.28s / 0.38s

   DIFF vs other pages:
   - Hero title  : CENTERED (like Tesla/Battery/OLA pages)
   - ef-date     : italic date label
   - ef-quote    : italic opening quote line
   - ef-subhead--bold : bold question subheads
   - ef-inline-img-wrap : left-aligned ~600px inline image
   - ef-tweet-img-wrap  : left-aligned ~600px tweet screenshot
   - ef-video-wrap      : thumbnail + custom play btn → inline iframe on click
   - Newsletter  : prop-based isMobile, Subscribe button
─────────────────────────────────────────────── */

const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800;900&family=Roboto:wght@300;400;500;700&display=swap');

  .ef-wrap *, .ef-wrap *::before, .ef-wrap *::after {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }
  .ef-wrap {
    font-family: 'Roboto', sans-serif;
    background: #1A1A1A;
    color: #ffffff;
    width: 100%;
    overflow-x: hidden;
  }
  .ef-inner {
    max-width: 1600px;
    margin: 0 auto;
    padding-left: 102px;
    padding-right: 102px;
    width: 100%;
  }
  .ef-content-col { width: 100%; }

  /* ── SCROLL FADE-UP ── */
  .ef-fade-up {
    opacity: 0;
    transform: translateY(40px);
    transition: opacity 0.7s ease, transform 0.7s ease;
  }
  .ef-fade-up.ef-in { opacity: 1; transform: translateY(0); }
  .ef-delay-1 { transition-delay: 0.08s; }
  .ef-delay-2 { transition-delay: 0.18s; }
  .ef-delay-3 { transition-delay: 0.28s; }
  .ef-delay-4 { transition-delay: 0.38s; }

  /* ══════════════════════════════════════
     HERO — centered title
  ══════════════════════════════════════ */
  .ef-hero { width: 100%; background: #1A1A1A; }
  .ef-hero__title-bar {
    width: 100%;
    background: #1A1A1A;
    padding: 60px 0px;
  }
  .ef-hero__title {
    font-family: 'Montserrat', sans-serif;
    font-size: 50px;
    font-weight: 700;
    color: #ffffff;
    line-height: 140%;
    text-align: center;
  }
  .ef-hero__img-wrap {
    width: 100%;
    max-height: 600px;
    overflow: hidden;
  }
  .ef-hero__img-wrap img {
    width: 100%;
    height: 100%;
    max-height: 600px;
    object-fit: cover;
    object-position: center;
    display: block;
  }

  /* ══════════════════════════════════════
     BODY CONTENT
  ══════════════════════════════════════ */
  .ef-body-section {
    width: 100%;
    background: #1A1A1A;
    padding: 60px 0 60px;
  }

  /* italic date label */
  .ef-date {
    font-family: 'Roboto', sans-serif;
    font-size: 16px;
    font-weight: 400;
    font-style: italic;
    line-height: 160%;
    color: #E2E2E2;
    margin-bottom: 16px;
  }

  /* italic opening quote */
  .ef-quote {
    font-family: 'Roboto', sans-serif;
    font-size: 16px;
    font-weight: 400;
    font-style: italic;
    line-height: 160%;
    color: #E2E2E2;
    margin-bottom: 24px;
  }

  .ef-para {
    font-family: 'Roboto', sans-serif;
    font-size: 16px;
    font-weight: 400;
    line-height: 160%;
    color: #E2E2E2;
    margin-bottom: 24px;
  }

  /* standard subhead */
  .ef-subhead {
    font-family: 'Roboto', sans-serif;
    font-size: 20px;
    font-weight: 500;
    color: #ffffff;
    line-height: 160%;
    margin-bottom: 14px;
    margin-top: 44px;
  }

  /* bold question subhead */
  .ef-subhead--bold {
    font-family: 'Roboto', sans-serif;
    font-size: 20px;
    font-weight: 500;
    color: #ffffff;
    line-height: 160%;
    margin-bottom: 14px;
    margin-top: 44px;
  }

  /* inline content image — left aligned ~600px */
  .ef-inline-img-wrap {
    margin: 44px 0;
    width: 683px;
    max-width: 100%;
  }
  .ef-inline-img-wrap img {
    width: 100%;
    height: auto;
    display: block;
    border-radius: 8px;
  }

  /* tweet screenshot — left aligned ~600px */
  .ef-tweet-img-wrap {
    margin: 44px 0;
    width: 683px;
    max-width: 100%;
  }
  .ef-tweet-img-wrap img {
    width: 100%;
    height: auto;
    display: block;
    border-radius: 12px;
  }

  /* ── VIDEO WRAPPER ── */
  .ef-video-wrap {
    margin: 44px 0;
    max-width: 100%;
    position: relative;
    border-radius: 10px;
    overflow: hidden;
  }

  /* thumbnail state — clickable */
  .ef-video-thumb {
    position: relative;
    cursor: pointer;
    display: block;
    border-radius: 10px;
    overflow: hidden;
  }
  .ef-video-thumb img {
    width: 100%;
    height: 500px;
    display: block;
    border-radius: 10px;
    transition: filter 0.25s ease;
  }
  .ef-video-thumb:hover img {
    filter: brightness(0.85);
  }

  /* custom play button — white circle centered */
  .ef-play-btn {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 64px;
    height: 64px;
    background: rgba(255,255,255,0.92);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;
    transition: background 0.2s ease, transform 0.2s ease;
  }
  .ef-video-thumb:hover .ef-play-btn {
    background: #ffffff;
    transform: translate(-50%, -50%) scale(1.08);
  }
  .ef-play-btn svg {
    margin-left: 4px;
  }

  /* iframe state — 16:9 responsive */
  .ef-video-iframe-wrap {
    position: relative;
    width: 100%;
    padding-bottom: 56.25%;
    height: 0;
    border-radius: 10px;
    overflow: hidden;
  }
  .ef-video-iframe-wrap iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border: none;
    border-radius: 10px;
  }

  /* ══════════════════════════════════════
     NEWSLETTER
  ══════════════════════════════════════ */
  .ef-newsletter {
    width: 100%;
    position: relative;
    overflow: hidden;
    min-height: 500px;
    display: flex;
    align-items: center;
  }
  .ef-newsletter__bg {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    
    object-position: center;
    z-index: 0;
  }
  .ef-newsletter__overlay {
    position: absolute;
    inset: 0;
    z-index: 1;
  }
  .ef-newsletter__inner {
    position: relative;
    z-index: 2;
    width: 100%;
    max-width: 1600px;
    margin: 0 auto;
    padding: 0px 102px;
  }
  .ef-newsletter__heading {
    font-family: 'Montserrat', sans-serif;
    font-size: 50px;
    font-weight: 700;
    color: #ffffff;
    line-height: 120%;
    margin-bottom: 14px;
  }
  .ef-newsletter__sub {
    font-family: 'Roboto', sans-serif;
    font-size: 15px;
    color: rgba(255,255,255,0.72);
    line-height: 160%;
    margin-bottom: 24px;
  }
  .ef-newsletter__form {
    display: flex;
    align-items: stretch;
    max-width: 552px;
    gap: 14px;
  }
  .ef-newsletter__input {
    flex: 1;
    height: 48px;
    background: #1A1A1A;
    border: 1px solid #ffffff;
    border-radius: 5px;
    padding: 15px 14px;
    font-family: 'Roboto', sans-serif;
    font-size: 16px;
    color: #FFFFFF;
    outline: none;
  }
  .ef-newsletter__input::placeholder { color: #FFFFFF; }
  .ef-newsletter__btn {
    height: 50px;
    padding: 15px 30px;
    background: #743FAC;
    border: none;
    border-radius: 5px;
    font-family: 'Roboto', sans-serif;
    font-size: 16px;
    font-weight: 500;
    color: #ffffff;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    white-space: nowrap;
    transition: background 0.25s ease;
  }
  .ef-newsletter__btn:hover { background: #5e2e90; }
  .ef-newsletter__btn-arrow {
    display: inline-flex;
    align-items: center;
    transition: transform 0.2s ease;
  }
  .ef-newsletter__btn:hover .ef-newsletter__btn-arrow { transform: translateX(4px); }
  .ef-newsletter__mic-img {
    width: 100%;
    height: auto;
    display: block;
    object-fit: cover;
  }
  .ef-newsletter__content { padding: 0; }

  /* ══════════════════════════
     RESPONSIVE 768px
  ══════════════════════════ */
  @media (max-width: 768px) {
    .ef-inner { padding-left: 32px; padding-right: 32px; }
    .ef-hero__img-wrap img { max-height: 350px; }
    .ef-hero__title-bar { padding: 24px 0px 20px; }
    .ef-hero__title { font-size: 24px; }
    .ef-body-section { padding: 40px 0 48px; }
    .ef-para { margin-bottom: 18px; font-size: 15px; }
    .ef-quote { font-size: 15px; margin-bottom: 18px; }
    .ef-date { font-size: 13px; margin-bottom: 14px; }
    .ef-subhead { font-size: 18px; margin-top: 28px; margin-bottom: 10px; }
    .ef-subhead--bold { font-size: 18px; margin-top: 28px; margin-bottom: 10px; }
    .ef-inline-img-wrap { width: 100%; margin: 28px 0; }
    .ef-tweet-img-wrap { width: 100%; margin: 28px 0; }
    .ef-video-wrap { width: 100%; margin: 28px 0; }

    .ef-newsletter {
      min-height: auto;
      align-items: stretch;
      flex-direction: column;
      background: #1A1A1A;
    }
    .ef-newsletter__mic-img { width: 100%; height: 350px; object-fit: cover; }
    .ef-newsletter__inner {
      padding: 0;
      display: flex;
      flex-direction: column;
    }
    .ef-newsletter__content {
      padding: 40px 24px 48px;
      text-align: center;
    }
    .ef-newsletter__heading { font-size: 24px; }
    .ef-newsletter__sub { font-size: 14px; margin: 0 auto 24px; }
    .ef-newsletter__form {
      flex-direction: column;
      max-width: 100%;
      gap: 12px;
    }
    .ef-newsletter__input { border-radius: 5px; height: 44px; }
    .ef-newsletter__btn {
      border-radius: 5px;
      justify-content: center;
      height: 44px;
    }
  }

  /* ══════════════════════════
     RESPONSIVE 480px
  ══════════════════════════ */
  @media (max-width: 480px) {
    .ef-inner { padding-left: 16px; padding-right: 16px; }
    .ef-hero__title-bar { padding: 30px 0px; }
    .ef-body-section { padding: 30px 0 30px; }
    .ef-hero__img-wrap img { max-height: 366px; width: 100%; }
    .ef-hero__title { font-size: 26px; line-height: 120%; }
    .ef-para { font-size: 14px; margin-bottom: 14px; }
    .ef-quote { font-size: 14px; margin-bottom: 14px; }
    .ef-date { font-size: 12px; margin-bottom: 12px; }
    .ef-subhead { margin-top: 30px; margin-bottom: 14px; font-size: 16px; }
    .ef-subhead--bold { margin-top: 30px; margin-bottom: 14px; font-size: 16px; }
    .ef-inline-img-wrap { width: 100%; margin: 30px 0; }
    .ef-tweet-img-wrap { width: 100%; margin: 30px 0; }
    .ef-video-wrap { width: 100%; margin: 30px 0; }

    .ef-newsletter {
      min-height: auto;
      align-items: stretch;
      flex-direction: column;
      background: #1A1A1A;
    }
    .ef-newsletter__mic-img { width: 100%; height: 366px; object-fit: cover; }
    .ef-newsletter__inner {
      padding: 0;
      display: flex;
      flex-direction: column;
    }
    .ef-newsletter__content {
      padding: 30px 16px 40px;
      text-align: center;
    }
    .ef-newsletter__heading { font-size: 30px; line-height: 120%; }
    .ef-newsletter__sub { font-size: 14px; max-width: 275px; margin: 0 auto 24px; }
    .ef-newsletter__form {
      flex-direction: column;
      max-width: 100%;
      gap: 15px;
      margin-top: 15px;
    }
    .ef-newsletter__input { border-radius: 5px; height: 40px; font-size: 14px; }
    .ef-newsletter__btn {
      border-radius: 5px;
      justify-content: center;
      height: 40px;
      font-size: 14px;
    }
  }
`;

/* ── SCROLL REVEAL ── */
function useScrollReveal() {
  useEffect(() => {
    const els = document.querySelectorAll(".ef-fade-up");
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add("ef-in"); }),
      { threshold: 0.12 }
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);
}

/* ── ARROW ── */
function ArrowRight() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

/* ── VIDEO PLAYER — thumbnail → inline iframe on click ── */
function VideoPlayer() {
  const [playing, setPlaying] = useState(false);
  const VIDEO_ID = "zIyEfWYLqio";

  return (
    <div className="ef-video-wrap ef-fade-up ef-delay-2">
      {!playing ? (
        /* Thumbnail + custom play button */
        <div
          className="ef-video-thumb"
          onClick={() => setPlaying(true)}
          role="button"
          aria-label="Play video"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setPlaying(true); }}
        >
          <img
            src={`https://img.youtube.com/vi/${VIDEO_ID}/maxresdefault.jpg`}
            alt="EVs catching fire video thumbnail"
          />
          <div className="ef-play-btn">
            <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
              <polygon points="7,4 19,11 7,18" fill="#1A1A1A" />
            </svg>
          </div>
        </div>
      ) : (
        /* Inline iframe — autoplay on click */
        <div className="ef-video-iframe-wrap">
          <iframe
            src={`https://www.youtube.com/embed/${VIDEO_ID}?autoplay=1`}
            title="EVs catching fire video"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      )}
    </div>
  );
}

/* ── NEWSLETTER ── */
function Newsletter({ isMobile }) {
  const [email, setEmail] = useState("");
  return (
    <section className="ef-newsletter">
      {!isMobile && (
        <>
          <img className="ef-newsletter__bg" src={NewsletterBgDesktop} alt="" aria-hidden="true" />
          <div className="ef-newsletter__overlay" />
        </>
      )}
      <div className="ef-newsletter__inner">
        {isMobile && (
          <img className="ef-newsletter__mic-img" src={NewsletterMicMobile} alt="Microphone" />
        )}
        <div className="ef-newsletter__content">
          <h2 className="ef-newsletter__heading">Sign up for our<br />newsletter!</h2>
          <p className="ef-newsletter__sub">Get notified about updates and be the first to get early access to new episodes.</p>
          <div className="ef-newsletter__form">
            <input
              className="ef-newsletter__input"
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <button className="ef-newsletter__btn">
              Subscribe
              <span className="ef-newsletter__btn-arrow"><ArrowRight /></span>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ── ROOT EXPORT ── */
export default function WhatIsTheDealWithEVsCatchingFireAndWhyShouldYouCare() {
  useScrollReveal();

  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    setIsMobile(mq.matches);
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  const [isSmallMobile, setIsSmallMobile] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 480px)");
    setIsSmallMobile(mq.matches);
    const handler = (e) => setIsSmallMobile(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  return (
    <>
      <style>{CSS}</style>
      <div className="ef-wrap">

        {/* HERO — centered title */}
        <section className="ef-hero">
          <div className="ef-hero__title-bar">
            <div className="ef-inner">
              <h1 className="ef-hero__title">What is the deal with EVs catching fire? And why should you care?</h1>
            </div>
          </div>
          <div className="ef-hero__img-wrap">
            <img
              src={isSmallMobile ? HeroImgMobile : HeroImg}
              alt="EVs catching fire"
            />
          </div>
        </section>

        {/* BODY */}
        <section className="ef-body-section">
          <div className="ef-inner">
            <div className="ef-content-col">

              <p className="ef-date ef-fade-up ef-delay-1">Energizing India Blog, 30th Dec '21</p>
              <p className="ef-quote ef-fade-up ef-delay-1">"Safety of E-Scooters continues to make headlines as EV fire claims its first life in India"</p>

              <p className="ef-para ef-fade-up ef-delay-1">For years, the idea of electric vehicles ruling our highways seemed like a far-fetched dream. However, electric vehicles (EVs) are now in high demand worldwide, and their popularity is only expected to grow in the coming years. Electric vehicles have seen rapid adoption as a result of Li-ion battery price reductions and improved performance. Despite the fact that electric vehicles are becoming increasingly popular due to their lower emissions and lower long-term costs, many people are unaware of their potential fire hazards.</p>

              <p className="ef-para ef-fade-up ef-delay-1">Global EV sales reached 2.1 million in 2019 with an overall stock of 7.2 million, indicating that the global EV fleet could grow to 116 million by 2030. EVs with Li-ion batteries are the most common; however, this well-established technology has safety issues related to thermal runaway, a phenomenon caused by cell abuse with fire and explosion consequences. A series of exothermic reactions causes the cells to overheat and emit flammable gas, which can spread from the cell level to the entire battery pack, resulting in fire or explosion. This effect can be triggered by a variety of root causes, including cell mechanical abuse, thermal abuse, or electrical abuse, etc.</p>

              <p className="ef-para ef-fade-up ef-delay-1">According to all indications, electric car battery fires are still uncommon, even when compared to gasoline and diesel fires. Though these incidents garner a lot of attention because electric vehicle technology is still considered to be in its infancy. In the meantime, incidents involving fire have been reported for EVs while charging or simply parking.</p>

              <p className="ef-para ef-fade-up ef-delay-1">However, these fires did not only happen with small EVs but also with luxury models on the road. Many such incidents have occurred globally, with brand companies such as Tesla, Porsche, Hyundai, General Motors, Volkswagen, Mitsubishi, Fisker Automotive, BYD, and others witnessing their electric vehicles catch fire.</p>

              <p className="ef-para ef-fade-up ef-delay-1">The first crash-related fire was reported in China in May 2012, when a high-speed car collided with a BYD e6 taxi in Shenzhen. In October 2013, two Tesla Model S incidents occurred: one involving a Model S catching fire after colliding with metal debris on a highway in Kent, Washington, and another involving a loss of control and collision with a tree in Merida, Mexico. Similarly, on September 1, 2015, a Nissan Leaf caught fire and was destroyed on a road in Flower Mound, Texas.</p>

              <p className="ef-para ef-fade-up ef-delay-1">The world witnessed numerous such incidents, which resulted in the eye-catching sense for this hazard of EVs catching fire. Following the massive incident, General Motors, Nissan, and Tesla released a guide for firefighters and first responders on how to properly handle a crashed electric-drive vehicle and safely disable its battery and other high voltage systems.</p>

              <p className="ef-para ef-fade-up ef-delay-1">While the source of a fire in a conventional vehicle can vary, the battery is almost always the source of a fire in an electric vehicle. While the powertrain is simple, the battery is the heart of an EV and is complex. Future technological advancements will make them even more so as different chemistries are used to increase energy density while reducing size and weight. On the other hand, a malfunction that could be caused by even one faulty cell could be fatal. In India alone, there have been six incidents of electric two-wheelers catching fire.</p>

              <div className="ef-inline-img-wrap ef-fade-up ef-delay-2">
                <img src={InlineImgScooters} alt="Electric scooters catching fire in India" />
              </div>

              <p className="ef-para ef-fade-up ef-delay-1">The first incident on Indian streets occurred in March 2021, outside the Vishwavidyalaya metro station on the university campus in north Delhi. Three electric scooter owners had plugged in their vehicles and left them on to charge. However, the charging station began to emit sparks, and fire quickly erupted. The fire destroyed three electric scooters. Following this incident, an electric scooter caught fire near IB Circle in Mancherial town while it was being driven down the street. Similarly, Pure EV's two electric scooters, ePluto, caught fire while running down the street. Later that month, in October, the scooter was from Okinawa, one of India's leading electric two-wheeler manufacturers. The scooter was parked in a garage and being charged at night when the blast occurred, according to CCTV footage. These incidents occur within a span of ten days, raising concerns about the safety of EVs in the event of a fire. Another electric scooter, this time a Sahara Evols X1 from the Sahara Group, caught fire in Mumbai's Andheri neighborhood. Following that, Gurgaon witnessed the death of a 60-year-old man and the grave injuries of two of his family members in what has been termed as India's first EV fire death, caused by an explosion of li-ion batteries in HCD India's electric 2-wheeler 'NPS cargo.'</p>

              <p className="ef-para ef-fade-up ef-delay-1">Albeit at times when EV vehicle sales in the country have increased. All of the fire incidents went viral in a matter of minutes, sparking a debate about battery safety and whether we were doing enough to ensure safety. Recent incidents of ePluto scooters catching fire have gone viral on social media; the manufacturer, ePluto, has blamed the media for exaggerating the incident, which has brought to light the potential dangers of EVs. However, the question of why EVs catch fire remains unanswered. What are the possible safety precautions? What level of awareness is required to address these issues? Finally, what regulations and measures should the government impose to ensure people's safety? Remains a major roadblock in the road to the future of electric vehicles in any country.</p>

              <div className="ef-tweet-img-wrap ef-fade-up ef-delay-2">
                <img src={TweetImg} alt="Tweet about EV fire incident" />
              </div>

              <h2 className="ef-subhead--bold ef-fade-up ef-delay-1">So, what exactly went wrong? Why do EV batteries catch fire?</h2>

              <p className="ef-para ef-fade-up ef-delay-1">The quality of the batteries, first and foremost. Even though electric vehicles are much simpler to build due to the lower number of moving parts, the vehicle's safety is entirely dependent on the quality of the batteries as well as the efficiency of the cooling system and battery management system. Battery fires are not uncommon in the automotive industry but determining the exact cause of the fire has always been difficult. A simple short circuit can cause a fire in today's automobile, which is a glorious tangle of wires. EVs, on the other hand, use larger and more complex lithium-ion batteries than ICE vehicles. Lithium batteries catch fire primarily when they are improperly manufactured or damaged, or when the software that operates the battery malfunctions.</p>

              <p className="ef-para ef-fade-up ef-delay-1">The use of organic liquid electrolytes, which are volatile and flammable when operating at high temperatures, is the primary weakness of lithium-ion batteries. Any severe external force, such as a collision, can cause chemical leakage. The media focused on the PURE EV case because a fire appears to have occurred in both scooters while they were parked on the road.</p>

              <p className="ef-para ef-fade-up ef-delay-1">Most lithium-ion battery packs used in electric vehicles in India are imported from China and are not manufactured in the country. While established legacy vehicle manufacturers have yet to commit to the expansion of electric vehicles in India, new inexperienced manufacturers are looking to grab the first-mover advantage, at times compromising safety and proper R&D to reduce costs.</p>

              <h2 className="ef-subhead ef-fade-up ef-delay-1">A worldwide issue!</h2>

              <p className="ef-para ef-fade-up ef-delay-1">Battery-related fires are not limited to India. They're also not particularly noteworthy. As the sale of electric vehicles has increased, so has the number of fires. Or, at the very least, that is what has occurred in other parts of the world. Earlier this year, General Motors expanded the recall of its Bolt EVs by 73,000 vehicles, GM had recalled the vehicle for the third time in nine months due to the risk of its high voltage battery catching fire. Even though only seven cars have caught fire so far, they have recalled over 100,000 vehicles at a cost of nearly $ 2 billion. Similarly, Hyundai recalled 82,000 electric vehicles last year after 13 fires were reported in its compact crossover Kona electric. This includes 456 units of the car that were sold in India.</p>

              <p className="ef-para ef-fade-up ef-delay-1">"It's a serious issue that needs to be solved," said Dave Sargent, head of the automotive practice at research firm J.D. Power. "It's something the industry will have to get a grip on if EVs are going to be the future."</p>

              <h2 className="ef-subhead ef-fade-up ef-delay-1">What's the answer?</h2>

              <p className="ef-para ef-fade-up ef-delay-1">As interest in EVs grows, there is a growing need for EV battery manufacturers to adhere to consistent standards and regulations. Risk thresholds must be measured, and OEMs must facilitate the procurement of battery units that exceed or meet those risk thresholds. Independent inspection units, as well as increased government regulation, are required to ensure that the batteries used in EVs do not pose an excessive fire risk.</p>

              <p className="ef-para ef-fade-up ef-delay-1">Simultaneously, the development of other battery units, particularly those that use fewer flammable materials, is required. OEMs should conduct additional research and development as they refine the design of battery units. Similarly, adequate training should be given to the first responders to counter and control fires caused by these lithium-ion batteries. Companies are already willing to collaborate with the government, but more proactive action is required to ensure that the EV industry's growing pains are addressed.</p>

              <p className="ef-para ef-fade-up ef-delay-1">Electric vehicles, as well as the technology that surrounds them, are all the rage. Everyone loves to talk about increased sales and a more environmentally friendly future. However, just because sales are increasing does not mean that these vehicles and technological advancements are safe. While manufacturers are likely doing their best to test for safety issues, there are numerous variables and unknowns. The burden of getting this right does not fall solely on manufacturers. Regulators must also take a more active role in establishing uniform standards and use reported incidents to proactively set more stringent safety codes for this immature technology. To do so, they must first be willing to understand what is wrong. And what needs to be done to resolve the problems. EVs are gaining popularity, and their mass production necessitates a safety pillar not only for the manufacturers but also for the end-users.</p>

              {/* Video — thumbnail with play button → plays inline on click */}
              <VideoPlayer />

            </div>
          </div>
        </section>

        {/* NEWSLETTER */}
        <Newsletter isMobile={isMobile} />

      </div>
    </>
  );
}